import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, User, Mail, Lock, Save, Loader2, Eye, EyeOff, ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import { authAPI } from '@/lib/api';
import { useAuth } from '@/context/AuthContext';

export default function MiCuenta() {
  const navigate = useNavigate();
  const { user, updateUser, logout } = useAuth();

  // Profile state
  const [nombre, setNombre] = useState(user?.nombre || '');
  const [apellidos, setApellidos] = useState(user?.apellidos || '');
  const [email, setEmail] = useState(user?.email || '');
  const [savingProfile, setSavingProfile] = useState(false);

  // Password state
  const [pwActual, setPwActual] = useState('');
  const [pwNueva, setPwNueva] = useState('');
  const [pwConfirm, setPwConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [savingPw, setSavingPw] = useState(false);

  const profileChanged =
    nombre !== (user?.nombre || '') ||
    apellidos !== (user?.apellidos || '') ||
    email.toLowerCase() !== (user?.email || '').toLowerCase();

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    if (!profileChanged) return;
    if (!nombre.trim()) {
      toast.error('El nombre no puede estar vacío');
      return;
    }
    setSavingProfile(true);
    try {
      const payload = {};
      if (nombre.trim() !== (user?.nombre || '')) payload.nombre = nombre.trim();
      if (apellidos !== (user?.apellidos || '')) payload.apellidos = apellidos;
      if (email.toLowerCase().trim() !== (user?.email || '').toLowerCase()) payload.email = email.toLowerCase().trim();

      const { data } = await authAPI.actualizarMiCuenta(payload);
      const updatedUser = data?.user || {};
      updateUser({
        nombre: updatedUser.nombre ?? payload.nombre ?? user?.nombre,
        apellidos: updatedUser.apellidos ?? payload.apellidos ?? user?.apellidos,
        email: updatedUser.email ?? payload.email ?? user?.email,
      });
      toast.success('Perfil actualizado correctamente');
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Error al actualizar el perfil';
      toast.error(typeof msg === 'string' ? msg : 'Error al actualizar el perfil');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (!pwActual || !pwNueva) {
      toast.error('Rellena la contraseña actual y la nueva');
      return;
    }
    if (pwNueva.length < 6) {
      toast.error('La nueva contraseña debe tener al menos 6 caracteres');
      return;
    }
    if (pwNueva !== pwConfirm) {
      toast.error('Las contraseñas nuevas no coinciden');
      return;
    }
    if (pwActual === pwNueva) {
      toast.error('La nueva contraseña debe ser distinta a la actual');
      return;
    }

    setSavingPw(true);
    try {
      await authAPI.cambiarMiPassword(pwActual, pwNueva);
      toast.success('Contraseña actualizada. Vuelve a iniciar sesión por seguridad.');
      setPwActual(''); setPwNueva(''); setPwConfirm('');
      // Forzar re-login para invalidar el token actual
      setTimeout(() => {
        logout();
        navigate('/crm/login');
      }, 1500);
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Error al cambiar la contraseña';
      toast.error(typeof msg === 'string' ? msg : 'Error al cambiar la contraseña');
    } finally {
      setSavingPw(false);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="mi-cuenta-page">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="-ml-2 shrink-0">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
              <User className="w-6 h-6 text-primary" />
              Mi cuenta
            </h1>
            <p className="text-sm text-muted-foreground mt-1">
              Gestiona tus datos personales y cambia tu contraseña
            </p>
          </div>
        </div>
        <Badge variant="outline" className="capitalize">
          <ShieldCheck className="w-3 h-3 mr-1" />
          {user?.role || 'usuario'}
        </Badge>
      </div>

      {/* Profile */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <User className="w-4 h-4 text-primary" />
            Datos personales
          </CardTitle>
          <CardDescription>Actualiza tu nombre, apellidos y email de inicio de sesión.</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSaveProfile} className="space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="mc-nombre">Nombre</Label>
                <Input
                  id="mc-nombre"
                  value={nombre}
                  onChange={(e) => setNombre(e.target.value)}
                  data-testid="mi-cuenta-nombre"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="mc-apellidos">Apellidos</Label>
                <Input
                  id="mc-apellidos"
                  value={apellidos}
                  onChange={(e) => setApellidos(e.target.value)}
                  data-testid="mi-cuenta-apellidos"
                />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="mc-email" className="flex items-center gap-1.5">
                <Mail className="w-3.5 h-3.5" />
                Email de inicio de sesión
              </Label>
              <Input
                id="mc-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                data-testid="mi-cuenta-email"
              />
              <p className="text-xs text-muted-foreground">
                Si cambias el email, lo usarás como nuevo usuario para iniciar sesión.
              </p>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={!profileChanged || savingProfile}
                className="gap-1.5"
                data-testid="mi-cuenta-guardar-perfil"
              >
                {savingProfile ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                {savingProfile ? 'Guardando…' : 'Guardar cambios'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Password */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base flex items-center gap-2">
            <Lock className="w-4 h-4 text-primary" />
            Cambiar contraseña
          </CardTitle>
          <CardDescription>
            Por seguridad, debes introducir tu contraseña actual. Tras cambiarla, se cerrará la sesión.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="pw-actual">Contraseña actual</Label>
              <div className="relative">
                <Input
                  id="pw-actual"
                  type={showPw ? 'text' : 'password'}
                  value={pwActual}
                  onChange={(e) => setPwActual(e.target.value)}
                  autoComplete="current-password"
                  data-testid="mi-cuenta-pw-actual"
                />
                <button
                  type="button"
                  onClick={() => setShowPw((s) => !s)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                  aria-label={showPw ? 'Ocultar contraseña' : 'Mostrar contraseña'}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label htmlFor="pw-nueva">Nueva contraseña</Label>
                <Input
                  id="pw-nueva"
                  type={showPw ? 'text' : 'password'}
                  value={pwNueva}
                  onChange={(e) => setPwNueva(e.target.value)}
                  autoComplete="new-password"
                  data-testid="mi-cuenta-pw-nueva"
                  minLength={6}
                />
                <p className="text-[10px] text-muted-foreground">Mínimo 6 caracteres</p>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="pw-confirm">Confirmar nueva</Label>
                <Input
                  id="pw-confirm"
                  type={showPw ? 'text' : 'password'}
                  value={pwConfirm}
                  onChange={(e) => setPwConfirm(e.target.value)}
                  autoComplete="new-password"
                  data-testid="mi-cuenta-pw-confirm"
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                disabled={savingPw || !pwActual || !pwNueva}
                className="gap-1.5"
                data-testid="mi-cuenta-guardar-pw"
              >
                {savingPw ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
                {savingPw ? 'Cambiando…' : 'Cambiar contraseña'}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
